import * as maptalks from "maptalks"
import { merge, isNumber, isArray } from "lodash"
import range from "lodash/range"
import { BaseObject } from "maptalks.three"
import { PlaneGeometry } from "three"
import { largestRect } from "d3plus-shape"

import {
  GroundLabelMaterialOptions,
  GroundLabelPlacementOptions,
  ResolveLabelScaleOptions,
} from "./types"

import {
  getGroundLabelMaterial,
  resolveLabelScale,
  mergePolygonRings,
} from "./utils"

const DEFAULT_OPTIONS = {
  interactive: false,
  altitude: 0,

  fontSize: 14,
  minLabelFontSize: 10,
  maxLabelFontSize: 42,
  fontFamily: "Manrope",
  fontWeight: 600,
  margin: 4,
  scaleMin: 0.5,
  lineHeight: 1.05,
  scaleStep: 0.05,
  textAlign: "center",
  textBaseline: "middle",
  fillStyle: "#000",
  maxLines: 4,
  pixelMultiplier: 4,

  /**
   * Base rect used to convert largestRect size into label scale.
   * When rect width/height matches these values, labelScale = 1.
   */
  baseRectWidth: 0.0006456122659,
  baseRectHeight: 0.0006456122659,
  maxLabelScale: 2.5,

  /**
   * fitToPlane: when true, the label plane scales to fill the full largestRect width
   * (matching v1 behavior). Text is rendered without wrapping, fitting the canvas width.
   *
   * When false (default), uses the constrained scale from resolveLabelScale
   * which respects both width and height of the rect — suited for occupant labels.
   */
  fitToPlane: false,
}

const defaultRectAngleToCalc = range(-90, 92, 2)

export type GroundLabelOptions = GroundLabelMaterialOptions &
  GroundLabelPlacementOptions &
  ResolveLabelScaleOptions

export class GroundLabel extends BaseObject {
  #angle = 0
  #bearing = 0
  #text = ""
  #offsetX = 0
  #offsetY = 0
  #originalPosition = null
  #layer = null
  #labelScale = 1

  constructor(rings: number[][][], options: GroundLabelOptions, layer: any) {
    options = maptalks.Util.extend({}, DEFAULT_OPTIONS, options, {
      layer,
      coordinate: rings[0],
    })

    const {
      altitude,
      text,
      fontSize,
      minLabelFontSize,
      maxLabelFontSize,
      fillStyle,
      textAlign,
      fontFamily,
      fontWeight,
      textBaseline,
      strokeStyle,
      lineWidth,
      lineHeight,
      margin,
      scaleMin,
      scaleStep,
      maxLines,
      pixelMultiplier,
      angle = defaultRectAngleToCalc,
      maxLabelScale,
      baseRectWidth,
      baseRectHeight,
      fitToPlane,
      offsetX = 0,
      offsetY = 0,
      ...properties
    } = options

    super()
    this._initOptions(options)
    this.properties = {
      ...properties,
      fillStyle,
      fontSize,
      minLabelFontSize,
      maxLabelFontSize,
      textAlign,
      textBaseline,
      fontFamily,
      fontWeight,
      strokeStyle,
      lineWidth,
      lineHeight,
      margin,
      scaleMin,
      scaleStep,
      maxLines,
      pixelMultiplier,
      baseRectWidth,
      baseRectHeight,
      maxLabelScale,
      fitToPlane,
    }

    this.#offsetX = offsetX
    this.#offsetY = offsetY
    this.#layer = layer

    const rectAngles = isArray(angle) ? angle : [angle]

    // Bridge any holes into the outer ring so largestRect avoids placing the rect over a hole.
    const outerRing = rings.length > 1 ? mergePolygonRings(rings) : rings[0]

    // Find the largest rectangle that fits inside the unit polygon.
    const rect = largestRect(outerRing, {
      cache: true,
      angle: rectAngles,
    })

    if (!rect) {
      throw new Error("GroundLabel: largestRect returned no result")
    }

    const { cx, cy, width, height, angle: calculatedAngle } = rect

    this.#text = text
    this.#angle = calculatedAngle

    /**
     * Scale strategy:
     * - fitToPlane: use width-only scale (v1 behavior) — fills the full rect width.
     * - default: use min(scaleX, scaleY) — constrained to both dimensions, better for dense labels.
     */
    const labelScale = fitToPlane
      ? Math.min(width / baseRectWidth, maxLabelScale)
      : resolveLabelScale(width, height, options)

    this.#labelScale = labelScale

    /**
     * Compute the final font size for the label text.
     *
     * 1. Scale the base font size using `labelScale` so larger units produce larger text.
     * 2. Ensure the font size never goes below `minLabelFontSize` to keep small-unit labels readable.
     * 3. Ensure the font size never exceeds `maxLabelFontSize` to prevent oversized labels on large units.
     */
    const targetFontSize = fitToPlane
      ? maxLabelFontSize
      : Math.min(
          Math.max(fontSize * labelScale, minLabelFontSize),
          maxLabelFontSize
        )

    const material = getGroundLabelMaterial(text, {
      ...merge({}, options, {
        fillStyle,
        fontSize: targetFontSize,
        minLabelFontSize,
        maxLabelFontSize,
        textAlign,
        textBaseline,
        fontFamily,
        fontWeight,
        strokeStyle,
        lineWidth,
        lineHeight,
        margin,
        scaleMin,
        scaleStep,
        maxLines,
        pixelMultiplier,
        fitToPlane,
      }),
    })

    material.needsUpdate = true

    const geometry = new PlaneGeometry(1, 1)
    this._createMesh(geometry, material)

    const z = layer.altitudeToVector3(altitude, altitude).x
    const basePosition = layer.coordinateToVector3({ x: cx, y: cy }, z)

    this.#originalPosition = basePosition.clone()
    const finalPosition = this.#calculateFinalPosition(basePosition)

    this.getObject3d().scale.set(labelScale, labelScale, labelScale)
    this.getObject3d().position.copy(finalPosition)
    this.getObject3d().rotation.z = (Math.PI / 180) * this.#angle
  }

  #calculateFinalPosition(basePosition: any) {
    if (this.#offsetX === 0 && this.#offsetY === 0) return basePosition

    const z = this.#layer.altitudeToVector3(0, 0).x
    const offsetVector = this.#layer.coordinateToVector3(
      { x: this.#offsetX, y: this.#offsetY },
      z
    )
    const zeroVector = this.#layer.coordinateToVector3({ x: 0, y: 0 }, z)

    return {
      x: basePosition.x + (offsetVector.x - zeroVector.x),
      y: basePosition.y + (offsetVector.y - zeroVector.y),
      z: basePosition.z,
    }
  }

  #updatePosition() {
    if (this.#originalPosition && this.#layer) {
      const finalPosition = this.#calculateFinalPosition(this.#originalPosition)
      this.getObject3d().position.copy(finalPosition)
    }
  }

  set bearing(value: number) {
    this.#bearing = value
    const degree = this.#angle + this.#bearing
    const angle = degree > 90 || degree < -90 ? this.#angle + 180 : this.#angle
    this.getObject3d().rotation.z = (Math.PI / 180) * angle
  }

  get angle() {
    return this.#angle
  }

  get currentAngle() {
    return this.#angle
  }

  get text() {
    return this.#text
  }

  get offsetX() {
    return this.#offsetX
  }

  get offsetY() {
    return this.#offsetY
  }

  get offset() {
    return { x: this.#offsetX, y: this.#offsetY }
  }

  set offsetX(value: number) {
    if (isNumber(value)) {
      this.#offsetX = value
      this.#updatePosition()
    }
  }

  set offsetY(value: number) {
    if (isNumber(value)) {
      this.#offsetY = value
      this.#updatePosition()
    }
  }

  set angle(newAngle: number) {
    if (isNumber(newAngle)) {
      this.#angle = newAngle
      this.getObject3d().rotation.z = (Math.PI / 180) * this.#angle
    }
  }

  setOffset(offsetX: number, offsetY: number) {
    if (isNumber(offsetX) && isNumber(offsetY)) {
      this.#offsetX = offsetX
      this.#offsetY = offsetY
      this.#updatePosition()
    }
  }

  addOffset(deltaX: number, deltaY: number) {
    if (isNumber(deltaX) && isNumber(deltaY)) {
      this.#offsetX += deltaX
      this.#offsetY += deltaY
      this.#updatePosition()
    }
  }

  resetOffset() {
    this.#offsetX = 0
    this.#offsetY = 0
    this.#updatePosition()
  }

  moveToPosition(targetX: number, targetY: number) {
    if (this.#originalPosition && this.#layer) {
      const currentCenter = this.#layer.vector3ToCoordinate(
        this.#originalPosition
      )
      this.#offsetX = targetX - currentCenter.x
      this.#offsetY = targetY - currentCenter.y
      this.#updatePosition()
    }
  }

  updateText(newText: string, options: GroundLabelMaterialOptions = {}) {
    this.#text = newText

    const mergedOptions = { ...this.properties, ...options }

    const targetFontSize = Math.min(
      Math.max(
        (mergedOptions.fontSize ?? DEFAULT_OPTIONS.fontSize) * this.#labelScale,
        mergedOptions.minLabelFontSize ?? DEFAULT_OPTIONS.minLabelFontSize
      ),
      mergedOptions.maxLabelFontSize ?? DEFAULT_OPTIONS.maxLabelFontSize
    )

    const newMaterial = getGroundLabelMaterial(newText, {
      ...mergedOptions,
      fontSize: targetFontSize,
    })

    this.getObject3d().material = newMaterial
    newMaterial.needsUpdate = true
  }
}
